import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  Typography,
  ButtonGroup,
} from '@mui/material';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import EventIcon from '@mui/icons-material/Event';
import apiClient, { getErrorMessage } from '../utils/axios';
import { formatNumber, formatKstDate } from '../utils/formatUtils';
import { useNotify } from './common';

const todayStr = () => new Date().toISOString().slice(0, 10);

/** 구독 부여. userId 가 없으면 회원 검색부터 */
export const GrantSubscriptionDialog = ({ open, user, onClose, onDone }) => {
  const notify = useNotify();
  const [plans, setPlans] = useState([]);
  const [target, setTarget] = useState(user || null);
  const [userOptions, setUserOptions] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [form, setForm] = useState({ planCode: '', startedAt: todayStr(), expiresAt: '', amount: '', paymentId: '', memo: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(user || null);
    setError('');
    setForm({ planCode: '', startedAt: todayStr(), expiresAt: '', amount: '', paymentId: '', memo: '' });
    apiClient
      .get('/admin/plans')
      .then(({ data }) => setPlans(data.plans.filter((p) => p.code !== 'free' && p.isActive)))
      .catch((e) => setError(getErrorMessage(e)));
  }, [open, user]);

  useEffect(() => {
    if (!open || user || userQuery.trim().length < 1) return undefined;
    const t = setTimeout(() => {
      apiClient
        .get('/admin/users', { params: { search: userQuery.trim(), pageSize: 20 } })
        .then(({ data }) => setUserOptions(data.items))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [userQuery, open, user]);

  const selectedPlan = plans.find((p) => p.code === form.planCode);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    if (!target) return setError('회원을 선택하세요.');
    if (!form.planCode) return setError('요금제를 선택하세요.');
    setBusy(true);
    setError('');
    try {
      await apiClient.post(`/admin/users/${encodeURIComponent(target.id)}/subscriptions`, {
        planCode: form.planCode,
        startedAt: form.startedAt ? new Date(`${form.startedAt}T00:00:00+09:00`).toISOString() : undefined,
        expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59+09:00`).toISOString() : undefined,
        amount: form.amount === '' ? undefined : Number(form.amount),
        paymentId: form.paymentId || undefined,
        memo: form.memo || undefined,
      });
      notify(`${target.username || target.email} 회원에게 구독을 부여했습니다.`);
      onDone?.();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <CardMembershipIcon color="primary" />
        구독 부여
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {user ? (
            <Typography>
              대상 회원: <b>{user.username}</b> ({user.email})
            </Typography>
          ) : (
            <Autocomplete
              options={userOptions}
              value={target}
              onChange={(_e, v) => setTarget(v)}
              onInputChange={(_e, v) => setUserQuery(v)}
              getOptionLabel={(o) => `${o.username || '-'} (${o.email}) #${o.id}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="회원 검색 (이메일·이름·ID)" />}
            />
          )}
          <TextField select label="요금제" value={form.planCode} onChange={set('planCode')}>
            {plans.map((p) => (
              <MenuItem key={p.code} value={p.code}>
                {p.name} ({p.billingCycleLabel}) · {formatNumber(p.price)}원
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="시작일" value={form.startedAt} onChange={set('startedAt')} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField
              type="date"
              label="만료일 (비우면 주기 자동)"
              value={form.expiresAt}
              onChange={set('expiresAt')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label="결제 금액"
              value={form.amount}
              onChange={set('amount')}
              placeholder={selectedPlan ? String(selectedPlan.price) : ''}
              fullWidth
            />
            <TextField label="결제 번호" value={form.paymentId} onChange={set('paymentId')} fullWidth />
          </Stack>
          <TextField label="메모 (예: 이벤트 무상 지급)" value={form.memo} onChange={set('memo')} multiline minRows={2} />
          <Alert severity="info">기존 활성 구독은 취소 처리되고 새 구독으로 바뀝니다.</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          취소
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          부여
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** 만료일 연장/변경 */
export const ExtendSubscriptionDialog = ({ open, subscription, onClose, onDone }) => {
  const notify = useNotify();
  const [days, setDays] = useState('30');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDays('30');
      setExpiresAt('');
      setError('');
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const body = expiresAt
        ? { expiresAt: new Date(`${expiresAt}T23:59:59+09:00`).toISOString() }
        : { days: Number(days) };
      const { data } = await apiClient.post(`/admin/subscriptions/${subscription.id}/extend`, body);
      notify(`만료일이 ${formatKstDate(data.subscription.expiresAt)} 로 변경되었습니다.`);
      onDone?.();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <EventIcon color="primary" />
        구독 기간 변경
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {subscription?.username || subscription?.userId} · {subscription?.planName} · 현재 만료일{' '}
            {formatKstDate(subscription?.expiresAt)}
          </Typography>
          <ButtonGroup size="small">
            {[7, 30, 90, 365].map((d) => (
              <Button
                key={d}
                variant={!expiresAt && Number(days) === d ? 'contained' : 'outlined'}
                onClick={() => {
                  setDays(String(d));
                  setExpiresAt('');
                }}
              >
                +{d}일
              </Button>
            ))}
          </ButtonGroup>
          <TextField
            type="number"
            label="연장 일수 (음수면 단축)"
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
              setExpiresAt('');
            }}
          />
          <TextField
            type="date"
            label="또는 만료일 직접 지정"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          취소
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          적용
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** 입금대기 신청 승인·거절 확인 (useConfirm 의 ask 사용) */
export const pendingActions = ({ ask, notify, onDone }) => {
  const run = async (sub, action) => {
    try {
      const res = await apiClient.post(`/admin/subscriptions/${sub.id}/${action}`);
      notify(res.data.message, res.data.ok === false ? 'warning' : 'success');
      onDone?.();
    } catch (e) {
      notify(getErrorMessage(e), 'error');
    }
  };
  const who = (sub) => (sub.username ? `${sub.username} 회원의 ` : '');
  return {
    approve: (sub) =>
      ask({
        title: '입금 확인 · 승인',
        message: `${who(sub)}${sub.planName}(${sub.billingCycleLabel}) 신청을 승인합니다.\n금액: ${
          sub.amount != null ? `${formatNumber(sub.amount)}원` : '-'
        }\n오늘부터 결제주기만큼 구독이 시작됩니다.`,
        confirmText: '승인',
        onConfirm: () => run(sub, 'approve'),
      }),
    reject: (sub) =>
      ask({
        title: '신청 거절',
        message: `${who(sub)}${sub.planName}(${sub.billingCycleLabel}) 신청을 거절합니다.`,
        confirmText: '거절',
        danger: true,
        onConfirm: () => run(sub, 'reject'),
      }),
  };
};
